import React from 'react';
import { TrendingUp, TrendingDown, Minus, ChevronRight } from 'lucide-react';
import type { SignalType, SignalStrength } from '../services/api';

interface SignalBadgeProps {
  signal: SignalType;
  strength: SignalStrength;
  confidence: number;
  size?: 'sm' | 'md' | 'lg';
  showConfidence?: boolean;
  onClick?: () => void;
  /** Name of the active strategy. When provided, renders a "via {strategyName}" label below the badge. */
  strategyName?: string;
  /** When true, renders a highlight ring to indicate this signal matches the user's active strategy. */
  strategyMatch?: boolean;
}

const SignalBadge: React.FC<SignalBadgeProps> = ({
  signal,
  strength,
  confidence,
  size = 'md',
  showConfidence = true,
  onClick,
  strategyName,
  strategyMatch,
}) => {
  const getSignalConfig = () => {
    switch (signal) {
      case 'strong_buy':
        return {
          label: 'STRONG BUY',
          icon: TrendingUp,
          bgColor: 'bg-emerald-500/30',
          textColor: 'text-emerald-300',
          borderColor: 'border-emerald-500/40',
          ringColor: 'ring-emerald-500/30',
        };
      case 'buy':
        return {
          label: 'BUY',
          icon: TrendingUp,
          bgColor: 'bg-emerald-500/20',
          textColor: 'text-emerald-400',
          borderColor: 'border-emerald-500/30',
          ringColor: 'ring-emerald-500/20',
        };
      case 'strong_sell':
        return {
          label: 'STRONG SELL',
          icon: TrendingDown,
          bgColor: 'bg-red-500/30',
          textColor: 'text-red-300',
          borderColor: 'border-red-500/40',
          ringColor: 'ring-red-500/30',
        };
      case 'sell':
        return {
          label: 'SELL',
          icon: TrendingDown,
          bgColor: 'bg-red-500/20',
          textColor: 'text-red-400',
          borderColor: 'border-red-500/30',
          ringColor: 'ring-red-500/20',
      };
      case 'neutral':
      case 'hold':
      default:
        return {
          label: 'HOLD',
          icon: Minus,
          bgColor: 'bg-amber-500/20',
          textColor: 'text-amber-400',
          borderColor: 'border-amber-500/30',
          ringColor: 'ring-amber-500/20',
        };
    }
  };

  const getStrengthLabel = () => {
    switch (strength) {
      case 'strong':
        return 'Strong';
      case 'moderate':
        return 'Moderate';
      case 'weak':
        return 'Weak';
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-2 py-1 text-xs gap-1',
          icon: 12,
          confidence: 'text-[10px]',
        };
      case 'lg':
        return {
          container: 'px-4 py-2 text-base gap-2',
          icon: 20,
          confidence: 'text-sm',
        };
      case 'md':
      default:
        return {
          container: 'px-3 py-1.5 text-sm gap-1.5',
          icon: 16,
          confidence: 'text-xs',
        };
    }
  };

  const config = getSignalConfig();
  const sizeClasses = getSizeClasses();
  const Icon = config.icon;

  const hasStrategy = strategyName !== undefined || strategyMatch !== undefined;

  const badge = (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`
        inline-flex items-center ${sizeClasses.container}
        ${config.bgColor} ${config.textColor} ${config.borderColor}
        border rounded-lg font-semibold
        ${onClick ? 'cursor-pointer hover:ring-2 ' + config.ringColor : 'cursor-default'}
        ${strategyMatch ? 'ring-2 ' + config.ringColor : ''}
        transition-all duration-200
      `}
    >
      <Icon size={sizeClasses.icon} />
      <span>{config.label}</span>
      {showConfidence && (
        <span className={`${sizeClasses.confidence} opacity-75`}>
          {confidence.toFixed(0)}%
        </span>
      )}
      {onClick && <ChevronRight size={sizeClasses.icon - 2} className="opacity-50" />}
    </button>
  );

  if (!hasStrategy) {
    return badge;
  }

  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      {badge}
      {strategyName && (
        <span className={`${sizeClasses.confidence} text-slate-400 truncate max-w-[140px]`}>
          via {strategyName}
        </span>
      )}
    </div>
  );
};

interface SignalCardProps {
  symbol: string;
  name?: string;
  signal: SignalType;
  strength: SignalStrength;
  confidence: number;
  price: number;
  changePercent: number;
  onClick?: () => void;
}

export const SignalCard: React.FC<SignalCardProps> = ({
  symbol,
  name,
  signal,
  strength,
  confidence,
  price,
  changePercent,
  onClick,
}) => {
  const getSignalBgGradient = () => {
    switch (signal) {
      case 'strong_buy':
      case 'buy':
        return 'from-emerald-500/10 to-transparent';
      case 'strong_sell':
      case 'sell':
        return 'from-red-500/10 to-transparent';
      default:
        return 'from-amber-500/10 to-transparent';
    }
  };

  return (
    <div
      onClick={onClick}
      className={`
        p-4 rounded-lg border border-slate-700/50
        bg-gradient-to-r ${getSignalBgGradient()}
        ${onClick ? 'cursor-pointer hover:border-slate-600' : ''}
        transition-all duration-200
      `}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-mono font-semibold text-teal-400">{symbol}</p>
          {name && <p className="text-xs text-slate-500 truncate max-w-[150px]">{name}</p>}
        </div>
        <SignalBadge signal={signal} strength={strength} confidence={confidence} size="sm" />
      </div>
      <div className="flex items-end justify-between">
        <div>
          <p className="text-lg font-mono font-semibold">${price.toLocaleString()}</p>
          <p className={`text-xs ${changePercent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-500">Strength</p>
          <p className="text-sm font-medium capitalize">{strength}</p>
        </div>
      </div>
    </div>
  );
};

interface SignalSummaryProps {
  buyCount: number;
  holdCount: number;
  sellCount: number;
  onFilterClick?: (filter: SignalType | null) => void;
  activeFilter?: SignalType | null;
}

export const SignalSummary: React.FC<SignalSummaryProps> = ({
  buyCount,
  holdCount,
  sellCount,
  onFilterClick,
  activeFilter,
}) => {
  const total = buyCount + holdCount + sellCount;

  const getBarWidth = (count: number) => {
    if (total === 0) return '33%';
    return `${(count / total) * 100}%`;
  };

  return (
    <div className="space-y-3">
      <div className="flex h-2 rounded-full overflow-hidden bg-slate-700">
        <div
          className="bg-emerald-500 transition-all duration-300"
          style={{ width: getBarWidth(buyCount) }}
        />
        <div
          className="bg-amber-500 transition-all duration-300"
          style={{ width: getBarWidth(holdCount) }}
        />
        <div
          className="bg-red-500 transition-all duration-300"
          style={{ width: getBarWidth(sellCount) }}
        />
      </div>
      <div className="flex justify-between text-sm">
        <button
          onClick={() => onFilterClick?.(activeFilter === 'buy' ? null : 'buy')}
          className={`flex items-center gap-2 px-2 py-1 rounded transition-colors ${
            activeFilter === 'buy' ? 'bg-emerald-500/20' : 'hover:bg-slate-700/50'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-emerald-400 font-medium">Buy</span>
          <span className="text-slate-400">({buyCount})</span>
        </button>
        <button
          onClick={() => onFilterClick?.(activeFilter === 'hold' ? null : 'hold')}
          className={`flex items-center gap-2 px-2 py-1 rounded transition-colors ${
            activeFilter === 'hold' ? 'bg-amber-500/20' : 'hover:bg-slate-700/50'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-amber-500" />
          <span className="text-amber-400 font-medium">Hold</span>
          <span className="text-slate-400">({holdCount})</span>
        </button>
        <button
          onClick={() => onFilterClick?.(activeFilter === 'sell' ? null : 'sell')}
          className={`flex items-center gap-2 px-2 py-1 rounded transition-colors ${
            activeFilter === 'sell' ? 'bg-red-500/20' : 'hover:bg-slate-700/50'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-red-400 font-medium">Sell</span>
          <span className="text-slate-400">({sellCount})</span>
        </button>
      </div>
    </div>
  );
};

export default SignalBadge;
